import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { ModalService } from 'src/app/shared/services/modal.service';
import { environment } from 'src/environments/environment';

/**
 * Stable error codes from the API. Branch on these, never on `message` - the API
 * owns that text and rewords it freely, so all user facing copy lives here.
 */
export const API_ERROR_CODE = {
  unauthorized: 'unauthorized',
  customerNotEntitled: 'customer_not_entitled'
} as const;

/**
 * Attaches the Okta ACCESS token to RelayNavigator API requests.
 *
 * Must be the access token, never the ID token - the API identifies RelayNav by
 * pinning the token's `cid` against an allowlist of this app's client IDs. The
 * auth servers are shared across Verisk apps, so `aud` does not identify us and
 * the ID token is not a valid API credential.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  // Re-authentication is a full page redirect. A burst of parallel requests can
  // all fail with 401 at once; only the first should start the redirect.
  private reauthenticating = false;

  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private modalService: ModalService,
    private router: Router
  ) { }

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isApiRequest(req.url) || this.isPublicHealthCheck(req)) {
      return next.handle(req);
    }

    // Resolved per request rather than once at construction, so a request issued
    // after a silent renewal picks up the refreshed token.
    return from(this.getAccessToken()).pipe(
      switchMap(token => token
        ? next.handle(this.withBearerToken(req, token))
        : throwError(() => this.missingTokenError(req))),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * A request with no token is never sent - the API would only reject it. It fails
   * locally as the same 401 the API would return, so handleError re-authenticates
   * and callers see exactly what they would have seen from the server.
   */
  private missingTokenError(req: HttpRequest<any>): HttpErrorResponse {
    return new HttpErrorResponse({
      error: { success: false, code: API_ERROR_CODE.unauthorized, message: 'No Okta access token available' },
      status: 401,
      statusText: 'Unauthorized',
      url: req.urlWithParams
    });
  }

  private withBearerToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  /**
   * Returns a valid access token, silently renewing it first if it has expired.
   * Never throws - null means no token, and the request is failed instead of sent.
   */
  private async getAccessToken(): Promise<string | null> {
    try {
      return await this.oktaAuth.getOrRenewAccessToken();
    } catch (error) {
      console.warn('Unable to obtain an Okta access token', error);
      return null;
    }
  }

  /**
   * True only for requests aimed at the configured API base URL - same origin
   * AND under the same base path. Matching on the full base URL keeps the token
   * away from Okta's endpoints, presigned S3 document URLs, static assets and
   * any other third party host.
   */
  private isApiRequest(url: string): boolean {
    const target = this.toUrl(url);
    const apiBase = this.toUrl(environment.server);

    if (!target || !apiBase || target.origin !== apiBase.origin) {
      return false;
    }

    const basePath = this.normalizePath(apiBase.pathname);
    const targetPath = this.normalizePath(target.pathname);

    return targetPath === basePath || targetPath.startsWith(`${basePath}/`);
  }

  /** The API's health check is a public GET at the root - it takes no token. */
  private isPublicHealthCheck(req: HttpRequest<any>): boolean {
    if (req.method !== 'GET') {
      return false;
    }

    const target = this.toUrl(req.url);
    const apiBase = this.toUrl(environment.server);
    if (!target || !apiBase) {
      return false;
    }

    const targetPath = this.normalizePath(target.pathname);
    return targetPath === '' || targetPath === this.normalizePath(apiBase.pathname);
  }

  private toUrl(url: string): URL | null {
    try {
      return new URL(url, window.location.origin);
    } catch {
      return null;
    }
  }

  private normalizePath(path: string): string {
    return path.replace(/\/+$/, '');
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const code = this.errorCode(error);

    if (code === API_ERROR_CODE.unauthorized || error.status === 401) {
      // Token missing (including one we could not obtain, see missingTokenError),
      // expired or invalid - the user needs to authenticate again.
      // The status is checked too, so a 401 from anywhere else in the stack (a
      // gateway, say) still recovers rather than leaving the user stuck.
      this.reauthenticate();
    } else if (code === API_ERROR_CODE.customerNotEntitled) {
      // Authenticated but not entitled to this customer. Deliberately no sign out
      // and no redirect to login - that would be a login loop.
      this.modalService.showError(
        'You do not have access to this customer. Please select a different customer or contact your administrator to request access.',
        'Access denied'
      );
    } else if (error.status === 403) {
      this.modalService.showError(
        'You do not have permission to perform this action.',
        'Access denied'
      );
    }

    return throwError(() => error);
  }

  private errorCode(error: HttpErrorResponse): string | null {
    return typeof error.error?.code === 'string' ? error.error.code : null;
  }

  private reauthenticate(): void {
    if (this.reauthenticating) {
      return;
    }
    this.reauthenticating = true;

    // Drop the unusable tokens so the guards and authState do not report the
    // user as authenticated while the redirect is in flight.
    this.oktaAuth.tokenManager.clear();
    // Same convention as OktaAuthGuard, so restoreOriginalUri brings the user back
    // to the page they were on.
    this.oktaAuth.setOriginalUri(this.router.url);

    this.oktaAuth.signInWithRedirect().catch(error => {
      this.reauthenticating = false;
      console.error('Failed to start Okta re-authentication', error);
    });
  }
}
